import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';

import { AppointmentsHttpService, Prescription, PrescriptionPlanning, PrescriptionPlanningStatus, PrescriptionTypes } from '@lib/prescriptions/data-access';
import { TranslatePipe } from '@ngx-translate/core';
import { take } from 'rxjs/operators';

import { MatIconButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogClose, MatDialogRef } from '@angular/material/dialog';
import { MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';

import { formatDate } from '@lib/commons/util-functions/format-date.function';

import { AppointmentStatus } from '@lib/core/models/visit.interface';

import { StatusComponent } from '@lib/shared-ui/commons/status/status.component';

import { CancelAppointmentModalComponent, CancelAppointmentModalData } from '../cancel-appointment-modal/cancel-appointment-modal.component';

@Component({
  selector: 'prescriptions-appointments-details-modal',
  standalone: true,
  imports: [MatDialogClose, MatIcon, StatusComponent, MatIconButton, MatSuffix, MatTooltip, TranslatePipe],
  templateUrl: './appointments-details-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppointmentsDetailsModalComponent {
  private readonly data: {
    memberId: string;
    prescriptionData: Prescription;
    plannings: PrescriptionPlanning[];
  } = inject(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef<AppointmentsDetailsModalComponent>);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly createAppointmentService = inject(AppointmentsHttpService);
  private readonly cdr = inject(ChangeDetectorRef);

  protected readonly formatDate = formatDate;
  protected readonly planningStatus = PrescriptionPlanningStatus;
  protected readonly prescriptionTypes = PrescriptionTypes;

  protected readonly plannings = signal<PrescriptionPlanning[]>(this.data.plannings);
  protected readonly expandedDetails = signal<Set<number>>(new Set());

  protected readonly prescription = computed<Prescription>(() => this.data.prescriptionData);
  protected readonly filteredPlannings = computed<PrescriptionPlanning[]>(() => {
    const latestPlanningsMap = new Map<string, PrescriptionPlanning>();
    const isLaboratory = this.prescription().type === PrescriptionTypes.LaboratoryService;
    const groupKey: keyof PrescriptionPlanning = isLaboratory ? 'orderId' : 'appointmentId';
    const willNotDoPlannings: PrescriptionPlanning[] = [];

    this.plannings().forEach(planning => {
      if (planning.planningStatus === PrescriptionPlanningStatus.WillNotDo) {
        willNotDoPlannings.push(planning);
        return;
      }

      const key = planning[groupKey] as string;
      if (!key) return;
      const existingPlanning = latestPlanningsMap.get(key);

      if (!existingPlanning) latestPlanningsMap.set(key, planning);
      else if (new Date(planning.lastUpdateDate) > new Date(existingPlanning.lastUpdateDate)) latestPlanningsMap.set(key, planning);
    });

    return [...Array.from(latestPlanningsMap.values()), ...willNotDoPlannings].sort((a, b) => new Date(b.lastUpdateDate).getTime() - new Date(a.lastUpdateDate).getTime());
  });

  protected togglePlannedDetails(index: number) {
    const expandedSet = new Set(this.expandedDetails());

    if (expandedSet.has(index)) expandedSet.delete(index);
    else expandedSet.add(index);
    this.expandedDetails.set(expandedSet);
  }

  protected cancelAppointment(planningId: string) {
    const selectedAppointment = this.filteredPlannings().find(planning => planning.id === planningId);
    if (!selectedAppointment) return;

    const modalData: CancelAppointmentModalData = {
      id: planningId,
      prescriptionId: this.data.prescriptionData.id,
      appointmentName: this.data.prescriptionData.name,
      prescription: this.data.prescriptionData,
    };

    const dialogRef = this.dialog.open(CancelAppointmentModalComponent, {
      data: modalData,
      width: '800px',
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) this.updatePlanningStatus(planningId, PrescriptionPlanningStatus.Cancelled);
    });
  }

  private updatePlanningStatus(planningId: string, newStatus: PrescriptionPlanningStatus) {
    const updatedPlannings = this.plannings().map(planning => {
      if (planning.id === planningId)
        return {
          ...planning,
          planningStatus: newStatus,
        };
      else return planning;
    });

    this.plannings.set(updatedPlannings);
    this.cdr.markForCheck();
  }

  protected viewAppointment(appointmentId: string) {
    const appointmentPlanning = this.filteredPlannings().find(planning => planning.appointmentId === appointmentId);

    this.router.navigate([`/members/${this.data.memberId}/prescriptions/${this.data.prescriptionData.id}/plannings/${appointmentPlanning.id}`]);
    this.dialogRef.close();
  }

  protected completeAppointment(appointmentId: string, planningId: string) {
    this.createAppointmentService
      .updateAppointmentStatus(appointmentId, AppointmentStatus.Completed)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.updatePlanningStatus(planningId, PrescriptionPlanningStatus.Completed);
          this.dialogRef.close({ type: 'complete', completed: true });
        },
        error: () => console.error('Failed to complete appointment'),
      });
  }

  protected completeLaboratoryOrder(appointmentId: string, planningId: string) {
    this.createAppointmentService
      .updateAppointmentStatus(appointmentId, AppointmentStatus.Completed)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.updatePlanningStatus(planningId, PrescriptionPlanningStatus.Completed);
          this.dialogRef.close({ type: 'complete', completed: true });
        },
        error: () => console.error('Failed to complete laboratory order'),
      });
  }
}
